import { getApiBaseUrl, getBackendAuthenticatedUser, logoutBackendSession } from "../backendAuth";
import { clearJwtToken, getValidJwtToken, setJwtToken } from "../authSession";
import type { User } from "../types";
import type { AuthProviderLoginInput, AuthProviderLoginResult, ExternalAuthUser, XentrosAuthProvider } from "./AuthProvider.interface";

async function loginWithUsm(input: AuthProviderLoginInput): Promise<AuthProviderLoginResult> {
  let response: Response;
  try {
    const base = getApiBaseUrl();
    response = await fetch(`${base}/api/auth/usm/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ username: input.identifier, password: input.password }),
    });
  } catch {
    return { ok: false, error: "Could not reach the authentication server." };
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let code: string | undefined;
    try { code = (JSON.parse(text) as { code?: string }).code; } catch { /* ignore */ }

    if (code === "USER_INACTIVE") {
      return { ok: false, error: "auth.account_inactive" };
    }
    return { ok: false, error: "auth.invalid" };
  }

  let loginData: { token?: string; user?: User } = {};
  try {
    loginData = await response.json() as { token?: string; user?: User };
  } catch { /* ignore */ }

  if (loginData.token) {
    setJwtToken(loginData.token);
  }

  // Fetch canonical user profile via /api/auth/me (Bearer token is now set)
  const sessionResult = await getBackendAuthenticatedUser();
  if (!sessionResult.ok || !sessionResult.user) {
    return { ok: false, error: sessionResult.error ?? "Session could not be established." };
  }

  return { ok: true, user: sessionResult.user as unknown as User, status: "authed" };
}

export const usmAuthProvider: XentrosAuthProvider = {
  key: "usm",

  login: loginWithUsm,

  async logout() {
    clearJwtToken();
    await logoutBackendSession();
  },

  async getCurrentUser() {
    const token = getValidJwtToken();
    if (!token) {
      return null;
    }

    const result = await getBackendAuthenticatedUser();
    if (!result.ok) {
      if (result.code === "NOT_AUTHENTICATED") {
        clearJwtToken();
      }
      return null;
    }
    // If the server returned a refreshed JWT (with up-to-date hosPermissions),
    // replace the stored token so future API calls use the current permissions.
    if (result.token && getValidJwtToken()) {
      setJwtToken(result.token);
    }
    return result.user as unknown as User;
  },

  async mapExternalUserToInternalUser(_externalUser: ExternalAuthUser) {
    return null;
  },
};
